import { AbstractSelfAccountKeyring } from './account_base';
import { AbstractPrivateKeyring } from './private';
import { PRIVATE_KEY_CRYPTO_CONTEXT } from '../context/private_key/__init__';
import { PrivateKeyConfigDumpArgs, PrivateKeyConfigLoadArgs } from '../context/private_key/args';
import { Curve25519KeyGenerator, Ed25519KeyGenerator } from '../base/key/ec';
import { ACCOUNT_ROOT_KEY_CRYPTO_CONTEXT } from '../context/account_key/root/__init__';
import {
  AccountRootKeyConfigDumpArgs,
  AccountRootKeyConfigLoadArgs,
} from '../context/account_key/root/args';
import { RandomKeyGenerator256 } from '../base/key/random';
import msgpack from 'msgpack-lite';
import { LOCAL_STORAGE_CRYPTO_CONTEXT } from '../context/local_storage/__init__';
import {
  LocalStorageConfigDumpArgs,
  LocalStorageConfigLoadArgs,
} from '../context/local_storage/args';
import { CryptoUpgrade, CU_PQC_ML, TARGET_ACCOUNT_KEYRING_VARIANT } from '../context/feature';
import { MLDSA87KeyGenerator, MLKEM1024KeyGenerator } from '../base/key/ml';

/**
 * This is a separate module to resolve a cyclic import.
 */
export class SelfAccountKeyring extends AbstractSelfAccountKeyring {
  /**
     * Loading the owner's account keyring is bootstrapped with the private-keyring. Following that the specific
        keys are all loaded with their own context, limiting the server's ability to be malicious. The keys are
        stored with authenticated encryption so they can be trusted if the loading succeeds.
     */
  public static async load(
    private_kr: AbstractPrivateKeyring,
    account_id: string,
    master_key: Uint8Array,
    encrypting_secret_ec: Uint8Array,
    signing_secret_ec: Uint8Array,
    // PQC extensions
    mlkem1024_secret?: Uint8Array,
    mldsa87_secret?: Uint8Array,
    mldsa87_public?: Uint8Array
  ): Promise<SelfAccountKeyring> {
    master_key = await PRIVATE_KEY_CRYPTO_CONTEXT.load(
      new PrivateKeyConfigLoadArgs(master_key, private_kr, this.MASTER)
    );
    return await this._load(
      master_key,
      account_id,
      encrypting_secret_ec,
      signing_secret_ec,
      mlkem1024_secret,
      mldsa87_secret,
      mldsa87_public
    );
  }

  /**
   * This can be used by the client to verify if a changed keyring (upgrade for example) loads successfully.
   */
  public static async reload_kr_asymmetric(
    master_kr: AbstractSelfAccountKeyring,
    encrypting_secret_ec: Uint8Array,
    signing_secret_ec: Uint8Array,
    // PQC extensions
    mlkem1024_secret?: Uint8Array,
    mldsa87_secret?: Uint8Array,
    mldsa87_public?: Uint8Array
  ): Promise<SelfAccountKeyring> {
    return await this._load(
      master_kr.get_secret_key(AbstractSelfAccountKeyring.MASTER),
      new TextDecoder().decode(master_kr.id),
      encrypting_secret_ec,
      signing_secret_ec,
      mlkem1024_secret,
      mldsa87_secret,
      mldsa87_public
    );
  }

  protected static async _load(
    master_key: Uint8Array,
    account_id: string,
    encrypting_secret_ec: Uint8Array,
    signing_secret_ec: Uint8Array,
    // PQC extensions
    mlkem1024_secret?: Uint8Array,
    mldsa87_secret?: Uint8Array,
    mldsa87_public?: Uint8Array
  ): Promise<SelfAccountKeyring> {
    const self_kr = new SelfAccountKeyring(
      account_id,
      {},
      {
        [SelfAccountKeyring.MASTER]: master_key,
      }
    );
    // delete master_key;
    let kr_variant = SelfAccountKeyring.VARIANT_BASE;
    let required_cu: CryptoUpgrade = null;
    if (mlkem1024_secret || mldsa87_secret || mldsa87_public) {
      if (!(mlkem1024_secret && mldsa87_secret && mldsa87_public)) {
        throw new Error('ML keys need to be all present');
      }
      kr_variant = SelfAccountKeyring.VARIANT_ML;
      required_cu = CU_PQC_ML;
    }
    // load secret keys and regenerate their public parts
    let curve25519keyPair = Curve25519KeyGenerator.regenerate_key_pair(
      await ACCOUNT_ROOT_KEY_CRYPTO_CONTEXT.load(
        new AccountRootKeyConfigLoadArgs(
          encrypting_secret_ec,
          self_kr,
          SelfAccountKeyring.CURVE25519,
          kr_variant
        ),
        required_cu
      )
    );
    let ed25519keyPair = Ed25519KeyGenerator.regenerate_key_pair(
      await ACCOUNT_ROOT_KEY_CRYPTO_CONTEXT.load(
        new AccountRootKeyConfigLoadArgs(
          signing_secret_ec,
          self_kr,
          SelfAccountKeyring.ED25519,
          kr_variant
        ),
        required_cu
      )
    );
    self_kr.update(
      {
        [SelfAccountKeyring.CURVE25519]: curve25519keyPair.publicKey,
        [SelfAccountKeyring.ED25519]: ed25519keyPair.publicKey,
      },
      {
        [SelfAccountKeyring.CURVE25519]: curve25519keyPair.secretKey,
        [SelfAccountKeyring.ED25519]: ed25519keyPair.secretKey,
      }
    );
    if (kr_variant === SelfAccountKeyring.VARIANT_ML) {
      let mlkemKeyPair = MLKEM1024KeyGenerator.regenerate_key_pair(
        await ACCOUNT_ROOT_KEY_CRYPTO_CONTEXT.load(
          new AccountRootKeyConfigLoadArgs(
            mlkem1024_secret,
            self_kr,
            SelfAccountKeyring.MLKEM1024,
            kr_variant
          ),
          required_cu
        )
      );
      let mldsaKeyPair = MLDSA87KeyGenerator.regenerate_key_pair(
        await ACCOUNT_ROOT_KEY_CRYPTO_CONTEXT.load(
          new AccountRootKeyConfigLoadArgs(
            mldsa87_secret,
            self_kr,
            SelfAccountKeyring.MLDSA87,
            kr_variant
          ),
          required_cu
        ),
        mldsa87_public
      );
      self_kr.update(
        {
          [SelfAccountKeyring.MLKEM1024]: mlkemKeyPair.publicKey,
          [SelfAccountKeyring.MLDSA87]: mldsaKeyPair.publicKey,
        },
        {
          [SelfAccountKeyring.MLKEM1024]: mlkemKeyPair.secretKey,
          [SelfAccountKeyring.MLDSA87]: mldsaKeyPair.secretKey,
        }
      );
    }
    return self_kr;
  }

  /**
     * Only returns the secret keys in encrypted form, even if public keys are loaded.
        NOTE: When changing a password only the 'master' key(s) must be saved/updated.
        NOTE: When creating the account, the public keys need to be signed with ACCOUNT_SIGNATURE_CRYPTO_CONTEXT!
     */
  public async dump(private_kr?: AbstractPrivateKeyring): Promise<Object> {
    let required_cu = null;
    if (this.get_variant() === AbstractSelfAccountKeyring.VARIANT_ML) {
      if (
        !this._secret_keys[AbstractSelfAccountKeyring.MLKEM1024] ||
        !this._secret_keys[AbstractSelfAccountKeyring.MLDSA87]
      ) {
        throw new Error('ML keys need to be all present');
      }
      required_cu = CU_PQC_ML;
    }
    let res = {
      [SelfAccountKeyring.CURVE25519]: await ACCOUNT_ROOT_KEY_CRYPTO_CONTEXT.dump(
        new AccountRootKeyConfigDumpArgs(this, SelfAccountKeyring.CURVE25519),
        required_cu
      ),
      [SelfAccountKeyring.ED25519]: await ACCOUNT_ROOT_KEY_CRYPTO_CONTEXT.dump(
        new AccountRootKeyConfigDumpArgs(this, SelfAccountKeyring.ED25519),
        required_cu
      ),
    };
    if (required_cu) {
      res[SelfAccountKeyring.MLKEM1024] = await ACCOUNT_ROOT_KEY_CRYPTO_CONTEXT.dump(
        new AccountRootKeyConfigDumpArgs(this, SelfAccountKeyring.MLKEM1024),
        required_cu
      );
      res[SelfAccountKeyring.MLDSA87] = await ACCOUNT_ROOT_KEY_CRYPTO_CONTEXT.dump(
        new AccountRootKeyConfigDumpArgs(this, SelfAccountKeyring.MLDSA87),
        required_cu
      );
    }
    if (private_kr) {
      res[SelfAccountKeyring.MASTER] = await PRIVATE_KEY_CRYPTO_CONTEXT.dump(
        new PrivateKeyConfigDumpArgs(this, private_kr, SelfAccountKeyring.MASTER)
      );
    }
    return res;
  }

  /**
   * The master-key (any version) must be created in a preparation step at registration.
   */
  public static generate_master_key(version?: string): Uint8Array {
    if (!version || version == SelfAccountKeyring.MASTER) {
      return RandomKeyGenerator256.new_key();
    } else {
      throw 'unknown key';
    }
  }

  /**
     * At registration this is done at the challenge step, when the encrypted master key is returned
        from the server. The master-key parameter is optional to support other (mostly development) uses.
     */
  public static generate(
    account_id: string,
    master_key?: Uint8Array,
    variant?: string
  ): SelfAccountKeyring {
    if (!master_key) {
      master_key = this.generate_master_key(SelfAccountKeyring.MASTER);
    } else if (master_key.byteLength != 32) {
      throw 'Invalid master key for current version';
    }

    let curve25519keyPair = Curve25519KeyGenerator.new_key();
    let ed25519keyPair = Ed25519KeyGenerator.new_key();

    let publicKeys = {};
    let secretKeys = {};

    publicKeys[SelfAccountKeyring.CURVE25519] = curve25519keyPair.publicKey;
    publicKeys[SelfAccountKeyring.ED25519] = ed25519keyPair.publicKey;

    secretKeys[SelfAccountKeyring.CURVE25519] = curve25519keyPair.secretKey;
    secretKeys[SelfAccountKeyring.ED25519] = ed25519keyPair.secretKey;
    secretKeys[SelfAccountKeyring.MASTER] = master_key;

    if ((variant || TARGET_ACCOUNT_KEYRING_VARIANT) === SelfAccountKeyring.VARIANT_ML) {
      this._generate_pqc_keys(publicKeys, secretKeys);
    }

    return new SelfAccountKeyring(account_id, publicKeys, secretKeys);
  }

  protected static _generate_pqc_keys(public_keys: Object, secret_keys: Object) {
    let mlkemKeyPair = MLKEM1024KeyGenerator.new_key();
    let mldsaKeyPair = MLDSA87KeyGenerator.new_key();
    public_keys[SelfAccountKeyring.MLKEM1024] = mlkemKeyPair.publicKey;
    public_keys[SelfAccountKeyring.MLDSA87] = mldsaKeyPair.publicKey;
    secret_keys[SelfAccountKeyring.MLKEM1024] = mlkemKeyPair.secretKey;
    secret_keys[SelfAccountKeyring.MLDSA87] = mldsaKeyPair.secretKey;
  }

  public upgrade(): undefined | SelfAccountKeyring {
    if (this.get_variant() === SelfAccountKeyring.VARIANT_BASE) {
      const public_keys = Object.assign({}, this._public_keys);
      const secret_keys = Object.assign({}, this._secret_keys);
      SelfAccountKeyring._generate_pqc_keys(public_keys, secret_keys);
      return new SelfAccountKeyring(new TextDecoder().decode(this.id), public_keys, secret_keys);
    }
  }

  /**
   * Creating an anonymous keyring consists only of confidentiality public-key cryptographic keys.
   */
  public static generate_anonymous(variant?: string): SelfAccountKeyring {
    let keys = Curve25519KeyGenerator.new_key();
    let secretObj = {};
    secretObj[SelfAccountKeyring.CURVE25519] = keys.secretKey;

    let publicObj = {};
    publicObj[SelfAccountKeyring.CURVE25519] = keys.publicKey;

    // NOTE: There is actually no use for MLKEM on the anonymous side, because the KEM can be used by the addressed
    // account's public MLKEM key only, but we add them to have a consistent variant on the keyring.
    if ((variant || TARGET_ACCOUNT_KEYRING_VARIANT) === SelfAccountKeyring.VARIANT_ML) {
      let mlkemKeyPair = MLKEM1024KeyGenerator.new_key();
      publicObj[SelfAccountKeyring.MLKEM1024] = mlkemKeyPair.publicKey;
      secretObj[SelfAccountKeyring.MLKEM1024] = mlkemKeyPair.secretKey;
    }

    return new SelfAccountKeyring(SelfAccountKeyring.ANONYMOUS_ID, publicObj, secretObj);
  }

  /**
     * Serialize and encrypt the keyring for local storage.
        NOTE: The key should be derived from a locally-stored and a server-stored input at least.
              For extra security the user could also provide some password for the protection of their session data.
              This is only beneficial when an attacker acquires their device while the app is not loaded.
     */
  public dump_local_storage(
    server_key: Uint8Array,
    client_key: Uint8Array,
    salt: Uint8Array
  ): Promise<Uint8Array> {
    let publicObj = {};
    if (this._public_keys[SelfAccountKeyring.MLDSA87]) {
      publicObj[SelfAccountKeyring.MLDSA87] = this._public_keys[SelfAccountKeyring.MLDSA87];
    }
    let secretObj = Object.assign({}, this._secret_keys);
    return LOCAL_STORAGE_CRYPTO_CONTEXT.dump(
      new LocalStorageConfigDumpArgs(
        msgpack.encode({
          id: new TextDecoder().decode(this.id),
          secret_keys: secretObj,
          public_keys: publicObj,
        }),
        server_key,
        client_key,
        salt
      )
    );
  }

  /**
   * Decrypt and load the keyring from local storage.
   */
  public static async load_local_storage(
    cipher: Uint8Array,
    server_key: Uint8Array,
    client_key: Uint8Array,
    salt: Uint8Array
  ): Promise<SelfAccountKeyring> {
    if (!salt) {
      salt = new Uint8Array([]);
    }
    const data_raw = await LOCAL_STORAGE_CRYPTO_CONTEXT.load(
      new LocalStorageConfigLoadArgs(cipher, server_key, client_key, salt)
    );
    const data = msgpack.decode(data_raw);
    const data_sks = data['secret_keys'];

    let curveKeys = Curve25519KeyGenerator.regenerate_key_pair(
      data_sks[SelfAccountKeyring.CURVE25519]
    );
    let edKeys = Ed25519KeyGenerator.regenerate_key_pair(data_sks[SelfAccountKeyring.ED25519]);

    let publicObj = {};
    publicObj[SelfAccountKeyring.CURVE25519] = curveKeys.publicKey;
    publicObj[SelfAccountKeyring.ED25519] = edKeys.publicKey;

    let secretObj = {};
    secretObj[SelfAccountKeyring.CURVE25519] = curveKeys.secretKey;
    secretObj[SelfAccountKeyring.ED25519] = edKeys.secretKey;
    secretObj[SelfAccountKeyring.MASTER] = data_sks[SelfAccountKeyring.MASTER];

    const data_pks = data['public_keys'] || {};
    if (
      data_sks[SelfAccountKeyring.MLKEM1024] ||
      data_sks[SelfAccountKeyring.MLDSA87] ||
      data_pks[SelfAccountKeyring.MLDSA87]
    ) {
      if (
        !(
          data_sks[SelfAccountKeyring.MLKEM1024] &&
          data_sks[SelfAccountKeyring.MLDSA87] &&
          data_pks[SelfAccountKeyring.MLDSA87]
        )
      ) {
        throw new Error('ML keys need to be all provided');
      }
      let mlkemKeyPair = MLKEM1024KeyGenerator.regenerate_key_pair(
        data_sks[SelfAccountKeyring.MLKEM1024]
      );
      let mldsaKeyPair = MLDSA87KeyGenerator.regenerate_key_pair(
        data_sks[SelfAccountKeyring.MLDSA87],
        data_pks[SelfAccountKeyring.MLDSA87]
      );
      publicObj[SelfAccountKeyring.MLKEM1024] = mlkemKeyPair.publicKey;
      publicObj[SelfAccountKeyring.MLDSA87] = mldsaKeyPair.publicKey;
      secretObj[SelfAccountKeyring.MLKEM1024] = mlkemKeyPair.secretKey;
      secretObj[SelfAccountKeyring.MLDSA87] = mldsaKeyPair.secretKey;
    }
    await this._custom_load_local_storage(data, secretObj, publicObj);
    return new SelfAccountKeyring(data['id'], publicObj, secretObj);
  }

  /**
   * Customization of loading keyring from local-storage.
   * Useful to be able to store additional secrets for example.
   */
  public static async _custom_load_local_storage(data, secret_keys, public_keys): Promise<void> {}
}
